import { observer } from 'mobx-react';
import {
  Button,
  ConfirmDialog,
  StandardDialog,
  TextField,
} from '@roc/ui';
import { Box, Typography } from '@material-ui/core';
import { WireAuthorizationForm } from './wireAuthorizationForm';
import { useDrawsStore } from '../hooks';
import { useEffect, useState } from 'react';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog } from '@roc/feature-general-tasks';

interface WireAuthorizationDialogProps {
  open: boolean;
  onClose: () => void;
  loanId: number;
  document: Document;
}

export const WireAuthorizationDialog = observer(({ open, onClose, loanId, document }: WireAuthorizationDialogProps) => {
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [actionsDisabled, setActionsDisabled] = useState(true);
  const [rejectReason, setRejectReason] = useState("");
  const { drawStore } = useDrawsStore();
  const wireAuthorizationFormStore = drawStore.wireAuthorizationFormStore;
  const taskId = document?.taskId;


  useEffect(() => {
    if (open) {
      const fetchBankInfo = async () => {
        await wireAuthorizationFormStore.fetchBankInfo(loanId);
        setActionsDisabled(!wireAuthorizationFormStore.initialData);
      }
      fetchBankInfo();
    }
    return () => {
      setActionsDisabled(true);
    }
  }, [wireAuthorizationFormStore, loanId, open]);

  const onReject = async () => {
    const result = await wireAuthorizationFormStore.rejectBankInfo(loanId, rejectReason);
    if (result) {
      setConfirmRejectOpen(false);
      setRejectReason("");
      onClose();
    }
  }

  const onApprove = async () => {
    const result = await wireAuthorizationFormStore.approveBankInfo(loanId);
    if (result) {
      setConfirmApproveOpen(false);
      onClose();
    }
  }

  const confirmRejectDialogContent = (
    <Box display="flex" flexDirection="column" gridGap="1rem">
      <Typography variant="body1">Are you sure you want to reject the bank information for this draw lender?</Typography>
      <TextField
        type="text"
        testId="rejectReason"
        standaloneLabel={false}
        label="Provide a reason for the rejection"
        variant="outlined"
        value={rejectReason}
        onChange={e => setRejectReason(e.target.value)}
        fullWidth
      />

    </Box>
  );

  const dialogContent = (
    < Box display="flex" flexDirection="column" gridGap="1rem">
      <WireAuthorizationForm store={wireAuthorizationFormStore} />
      <Box display="flex" justifyContent="flex-end" alignItems="center" gridGap="1rem">
        <Button
          testId="cancel"
          variant="outlined"
          color="primary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          testId="reject"
          variant="outlined"
          color="primary"
          onClick={() => { setConfirmRejectOpen(true) }}
          disabled={actionsDisabled}
        >
          Reject
        </Button>
        <Button
          testId="approve"
          variant="outlined"
          color="primary"
          onClick={() => { setConfirmApproveOpen(true) }}
          disabled={actionsDisabled}
        >
          Approve
        </Button>
      </Box>
      <ConfirmDialog
        open={confirmApproveOpen}
        title={'Approve Draw Lender Bank information'}
        body={'Are you sure you want to approve the bank information for this draw lender?'}
        handleCancel={() => setConfirmApproveOpen(false)}
        handleClose={() => setConfirmApproveOpen(false)}
        handleConfirm={onApprove}
        confirmButtonText={'Confirm'}
        cancelButtonText={'Cancel'}
      />
      <ConfirmDialog
        open={confirmRejectOpen}
        title={'Reject Draw Lender Bank information'}
        body={confirmRejectDialogContent}
        handleCancel={() => {
          setConfirmRejectOpen(false);
          setRejectReason("");
        }}
        handleClose={() => setConfirmRejectOpen(false)}
        handleConfirm={onReject}
        confirmButtonText={'Confirm'}
        cancelButtonText={'Cancel'}
      />
    </Box>
  );

  return (
    <>
      {taskId ? (
        <CustomTaskDialog
          open={open}
          title="Wire Authorization"
          handleClose={onClose}
          dialogContent={dialogContent}
          taskId={taskId}
        />
      ) : (
        <StandardDialog
          open={open}
          title="Wire Authorization"
          maxWidth="md"
          handleClose={onClose}
          dialogContent={dialogContent}
        />
      )}
    </>
  )
});
